import { inject, Injectable, Injector, signal } from '@angular/core';

import { LoggerContext } from '@ph-core/enums/logger-context.enum';
import { LoggerService } from '@ph-core/services/logger/logger.service';
import { UserStore } from '@ph-store/user/user.store';

import {
  DealerContextChangedMessage,
  DealerContextSnapshot,
  PHOENIX_DEALER_CONTEXT_CHANGED,
  PHOENIX_DEALER_CONTEXT_CHANNEL,
} from './dealer-context-sync.constants';

interface PersistableStore {
  writeToStorage?: () => void;
}

/**
 * One dealer is active per browser profile. Emulation updates the shared session cookie
 * immediately, while other tabs keep rendering the previous dealer.
 * BroadcastChannel notifies those tabs. A sibling tab blocks its UI until the user reloads
 * onto the dealer that was just persisted. The sending tab does not receive its own message.
 */
@Injectable({ providedIn: 'root' })
export class DealerContextSyncService {
  private injector = inject(Injector);
  private logger = inject(LoggerService);

  /** Set when another tab changed the active dealer. Null while this tab can keep working. */
  readonly blockedDealer = signal<DealerContextSnapshot | null>(null);

  private broadcastChannel: BroadcastChannel | undefined;

  initCrossTabDealerListener(): void {
    if (this.broadcastChannel || typeof BroadcastChannel === 'undefined') {
      return;
    }

    try {
      this.broadcastChannel = new BroadcastChannel(PHOENIX_DEALER_CONTEXT_CHANNEL);
      this.broadcastChannel.onmessage = (event: MessageEvent<DealerContextChangedMessage>) => {
        this.handleDealerContextChanged(event.data);
      };
    } catch {
      /* ignore */
    }
  }

  /**
   * Persist dealer-scoped stores before posting so a sibling tab's reload reads the new dealer,
   * not the snapshot still waiting on a storage effect.
   */
  broadcastDealerContext(context: DealerContextSnapshot, storesToPersist: readonly PersistableStore[] = []): void {
    for (const persisted of storesToPersist) {
      try {
        persisted.writeToStorage?.();
      } catch (error) {
        this.logger.warn(LoggerContext.Auth, 'Could not persist dealer state before cross-tab broadcast.', { error });
      }
    }

    const message: DealerContextChangedMessage = {
      type: PHOENIX_DEALER_CONTEXT_CHANGED,
      cmsDealerNumber: context.cmsDealerNumber ?? '',
      dealerId: context.dealerId ?? 0,
    };

    try {
      const channel = this.broadcastChannel ?? new BroadcastChannel(PHOENIX_DEALER_CONTEXT_CHANNEL);
      channel.postMessage(message);
      if (!this.broadcastChannel) {
        channel.close();
      }
      this.logger.log(LoggerContext.Auth, 'BroadcastChannel dealer context change sent.', {
        cmsDealerNumber: message.cmsDealerNumber,
        dealerId: message.dealerId,
      });
    } catch (error) {
      this.logger.warn(LoggerContext.Auth, 'BroadcastChannel unavailable for dealer context change.', { error });
    }
  }

  handleDealerContextChanged(message: DealerContextChangedMessage | undefined): void {
    if (message?.type !== PHOENIX_DEALER_CONTEXT_CHANGED) {
      return;
    }

    const userStore = this.injector.get(UserStore);
    if (!userStore.isLoggedIn()) {
      return;
    }

    const user = userStore.user();
    const currentCmsDealerNumber = user?.cmsDealerNumber?.trim() ?? '';
    const nextCmsDealerNumber = message.cmsDealerNumber?.trim() ?? '';
    const currentDealerId = user?.dealerInfo?.dealerId ?? 0;
    const nextDealerId = message.dealerId ?? 0;
    if (currentCmsDealerNumber === nextCmsDealerNumber && currentDealerId === nextDealerId) {
      return;
    }

    this.logger.log(LoggerContext.Auth, 'Dealer context changed in another tab — blocking this tab.', {
      previousCmsDealerNumber: currentCmsDealerNumber,
      nextCmsDealerNumber,
      nextDealerId,
    });
    this.blockedDealer.set({
      cmsDealerNumber: nextCmsDealerNumber,
      dealerId: nextDealerId,
    });
  }
}
